/*
 * Copyright (c) 2016 Omnigon Communications, LLC. All rights reserved.
 *
 * This software is the confidential and proprietary information of Omnigon Communications, LLC
 * ("Confidential Information"). You shall not disclose such Confidential Information and shall access and use it only
 * in accordance with the terms of the license agreement you entered into with Omnigon Communications, LLC, its
 * subsidiaries, affiliates or authorized licensee. Unless required by applicable law or agreed to in writing, this
 * Confidential Information is provided on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 * express or implied. See the license agreement for the specific language governing permissions and limitations.
 */

import can from 'can/util/util';
import Component from 'can/component/component';
import template from './leaderboard-mini.stache';
import DomEventsManager from 'scripts/dom-events-manager';
import ViewModel from './leaderboard-mini.model';
import FeedService from 'scripts/services/base-feed-service';
import dateTime from 'scripts/utils/date-time';
import compare from 'vendors/compare-property/compare-property';
import PlayerService from 'scripts/services/player-service';
import dtm from 'scripts/services/dtm';

const leaderboardFeedUrl = window.globalSettings.feeds.smt.leaderboardUrl;

Component.extend({
    tag: 'leaderboard-mini',
    template: template,
    viewModel: ViewModel,
    events: {
        inserted: function() {
            this.standingsSorter = this.getStandingsSorter();
            this.fetchLeaderboard();
            DomEventsManager.subscribe(this);
        },
        getStandingsSorter() {
            return compare.properties({
                'position.value': 1,
                'toPar.value': -1,
                teeTime: 1,
            });
        },
        sortStandings: function(standings) {
            return _(standings || [])
                .thru((list) => {
                    list.sort(this.standingsSorter);
                    return list;
                })
                .value();
        },

        recalculateFavorites(data) {
            let favorites = [];
            let favoriteIds = this.viewModel.getFavoritesList();
            _.forEach(_.get(data, 'standings', []), (standing) => {
                let playerId = _.get(standing, 'player.identifier');
                if (favoriteIds.indexOf(playerId) != -1) {
                    favorites.push(standing);
                    standing.isFav = true;
                }
            });
            let sortedFavorites = this.sortStandings(favorites);
            this.viewModel.attr('favorites', sortedFavorites);
            this.viewModel.attr('hasFavorite', Boolean(sortedFavorites.length));
        },

        fetchLeaderboard: function() {
            this.leaderboardFeedService = new FeedService({
                feedUrl: leaderboardFeedUrl,
                fromCache: false,
            });

            this.leaderboardFeedService
                .on('fetching', (ev, value) =>
                    this.viewModel.attr('fetching', value),
                )
                .on('data', (ev, data) => {
                    const standings = this.sortStandings(
                        _.castArray(_.get(data, 'standings', [])).map(
                            (standing) => {
                                standing.teeTime = dateTime.getTimeDiffFromNightInStr(
                                    _.get(standing, 'holesThroughDate'),
                                );
                                standing.hasTeeTime = !_.isNil(
                                    standing.teeTime,
                                );
                                standing.noTeeTime = !standing.hasTeeTime;
                                return standing;
                            },
                        ),
                    );

                    const previousStandings = this.viewModel.attr('standings');
                    const newStandingsMap = {};
                    const previousStandingsMap = {};
                    const removedIndexes = [];
                    this.viewModel.attr('data', data);

                    if (this.viewModel.attr('isLive')) {
                        this.recalculateFavorites(data);
                    }

                    standings.forEach((standing) => {
                        newStandingsMap[standing.player.identifier] = standing;
                        standing.isCut =
                            ['WD', 'MC', 'DQ', 'DNS'].indexOf(
                                _.get(standing, 'position.displayValue'),
                            ) > -1;
                    });

                    can.batch.start();
                    if (!previousStandings) {
                        this.viewModel.attr(
                            'standings',
                            this.sortStandings(standings),
                        );
                    } else {
                        previousStandings.forEach((standing, index) => {
                            const playerId = _.get(
                                standing,
                                'player.identifier',
                            );

                            if (newStandingsMap[playerId]) {
                                previousStandingsMap[playerId] = standing;
                            } else {
                                removedIndexes.push(index);
                            }
                        });

                        _.reduce(
                            removedIndexes,
                            (accum, index) => {
                                previousStandings.splice(index - accum, 1);
                                return accum + 1;
                            },
                            0,
                        );

                        _.forEach(standings, (standing, index) => {
                            const playerId = _.get(
                                standing,
                                'player.identifier',
                            );
                            const previousStanding =
                                previousStandingsMap[playerId];

                            if (previousStanding) {
                                _.each(standing, (value, key) => {
                                    previousStanding.attr(key, value);
                                });
                                _.each(previousStanding, (value, key) => {
                                    if (standing[key] === undefined) {
                                        previousStanding.removeAttr(key);
                                    }
                                });
                            } else {
                                previousStandings.push(standing);
                            }
                        });

                        this.viewModel.attr(
                            'standings',
                            this.sortStandings(previousStandings),
                        );
                    }
                    can.batch.stop();
                });
            this.leaderboardFeedService.fetch();
        },

        '.icon-favorite-star click'(element, event) {
            event.stopPropagation();

            let playerId = element.data('identifier');
            let playerName = element.data('name');
            let closestRow = element.closest('tr');
            PlayerService.toggleFavorite(playerId.toString());
            if (
                !closestRow.hasClass('is-favorite') &&
                !closestRow.hasClass('is-user-favorite')
            ) {
                dtm.trackLeaderboardFavoriting(playerName);
            }

            const star = element.closest('.icon-favorite-star');
            const allStars = $(`[data-identifier='${playerId}']`);

            if (star.hasClass('is-user-favorite')) {
                allStars.removeClass('is-user-favorite');
            } else {
                allStars.addClass('is-user-favorite');
            }

            this.recalculateFavorites(this.viewModel.attr('data'));
            this.viewModel.dispatch('favToggled');
        },
    },
});
